// DOM builders for the panel. No state and no network: every function
// returns elements; widget.ts wires them to the store and the controller.

import { ICON_ATTACH, ICON_BACK, ICON_BUBBLE, ICON_CLOSE, ICON_HISTORY, ICON_SEND, ICON_THUMBS_DOWN, ICON_THUMBS_UP } from './icons'
import type { Conversation, Theme, Visitor } from './types'

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  if (className) node.className = className
  if (text !== undefined) node.textContent = text
  return node
}

function button(className: string, label: string, html?: string): HTMLButtonElement {
  const b = el('button', className)
  b.type = 'button'
  b.setAttribute('aria-label', label)
  if (html) b.innerHTML = html
  else b.textContent = label
  return b
}

export function buildBubble(icon: string): HTMLButtonElement {
  const bubble = button('bubble', 'Open chat', icon)
  bubble.setAttribute('aria-haspopup', 'dialog')
  bubble.setAttribute('aria-expanded', 'false')
  return bubble
}

export interface PanelRefs {
  root: HTMLElement
  logo: HTMLImageElement
  /** The demo disclaimer; hidden unless the account is a demo. */
  demoNote: HTMLElement
  title: HTMLElement
  sub: HTMLElement
  backBtn: HTMLButtonElement
  historyBtn: HTMLButtonElement
  closeBtn: HTMLButtonElement
  transcript: HTMLElement
  liveRegion: HTMLElement
  error: HTMLElement
  composer: HTMLElement
  textarea: HTMLTextAreaElement
  sendBtn: HTMLButtonElement
  attachBtn: HTMLButtonElement
  fileInput: HTMLInputElement
  /** Preview of the image about to be sent; empty when there is none. */
  attachment: HTMLElement
}

let panelSeq = 0

export function buildPanel(theme: Theme): PanelRefs {
  const id = `airys-panel-${++panelSeq}`
  const root = el('div', 'panel')
  root.setAttribute('role', 'dialog')
  root.setAttribute('aria-modal', 'false')
  root.setAttribute('aria-labelledby', `${id}-title`)

  const header = el('div', 'header')
  const backBtn = button('header-btn header-back', 'Back', ICON_BACK)
  backBtn.hidden = true
  const left = el('div', 'header-left')
  // Built whether or not there is a logo yet: the admin preview sets one
  // after the fact, and a broken src would flash a placeholder.
  const logo = el('img', 'header-logo')
  logo.alt = ''
  logo.hidden = !theme.logoUrl
  if (theme.logoUrl) logo.src = theme.logoUrl
  left.appendChild(logo)
  const text = el('div', 'header-text')
  const title = el('span', 'header-title', theme.title)
  title.id = `${id}-title`
  const sub = el('span', 'badge badge-neutral', 'Replies right away')
  text.append(title, sub)
  left.appendChild(text)
  // History is only for verified visitors; the button stays out of the
  // chrome entirely for everyone else.
  const historyBtn = button('header-btn header-history', 'Past conversations', ICON_HISTORY)
  historyBtn.hidden = true
  const closeBtn = button('header-btn header-close', 'Close chat', ICON_CLOSE)
  const headerActions = el('div', 'header-actions')
  headerActions.append(historyBtn, closeBtn)
  header.append(backBtn, left, headerActions)

  // One line under the header, for an account that is a demo of a real
  // company's help center. Above the transcript rather than in the footer:
  // it has to be read before anything is typed, not after.
  const demoNote = el(
    'div',
    'demo-note',
    "Demo by airys. Don't share real account or payment details.",
  )
  demoNote.hidden = !theme.demo

  const transcript = el('div', 'transcript')
  transcript.setAttribute('role', 'log')
  transcript.setAttribute('aria-live', 'off')

  // Screen readers hear new replies and notices from here, not from the log.
  const liveRegion = el('div', 'sr-only')
  liveRegion.setAttribute('aria-live', 'polite')

  const error = el('div', 'error')
  error.setAttribute('role', 'alert')
  error.hidden = true

  // One box: what you type, and the two things you can do with it.
  const composer = el('div', 'composer')
  const wrap = el('div', 'composer-input-wrap')
  const attachment = el('div', 'attachment-chip')
  attachment.hidden = true
  const textarea = el('textarea')
  textarea.placeholder = 'Type your question…'
  textarea.rows = 1
  textarea.setAttribute('aria-label', 'Your message')
  const row = el('div', 'composer-row')
  const actions = el('div', 'composer-actions')
  const fileInput = el('input')
  fileInput.type = 'file'
  fileInput.accept = 'image/*'
  fileInput.hidden = true
  const attachBtn = button('composer-btn attach', 'Attach an image', ICON_ATTACH)
  const sendBtn = button('composer-btn send', 'Send', ICON_SEND)
  actions.append(attachBtn, sendBtn)
  row.append(textarea, actions)
  wrap.append(attachment, row)
  composer.append(wrap, fileInput)

  const footer = el('div', 'composer-footer')
  footer.append('Powered by ')
  const brand = el('a', undefined, 'airys')
  brand.href = 'https://airys.ai'
  brand.target = '_blank'
  brand.rel = 'noopener noreferrer'
  footer.appendChild(brand)

  root.append(header, demoNote, transcript, liveRegion, error, composer, footer)
  return { root, logo, title, sub, demoNote, backBtn, historyBtn, closeBtn, transcript, liveRegion, error, composer, textarea, sendBtn, attachBtn, fileInput, attachment }
}

// The image waiting in the composer, with a way to drop it.
export function buildAttachmentChip(previewUrl: string, name: string, onRemove: () => void): HTMLElement {
  const chip = el('div', 'attachment-chip-inner')
  const img = el('img')
  img.src = previewUrl
  img.alt = name
  const label = el('span', 'attachment-name', name)
  const remove = button('attachment-remove', 'Remove image', ICON_CLOSE)
  remove.addEventListener('click', onRemove)
  chip.append(img, label, remove)
  return chip
}

/**
 * The greeting is the whole welcome. A headline of our own would sit above
 * it saying the same thing, in English, whatever language they wrote in.
 */
export function buildWelcome(greeting: string): HTMLElement {
  const w = el('div', 'welcome')
  const icon = el('div', 'welcome-icon')
  icon.innerHTML = ICON_BUBBLE
  icon.setAttribute('aria-hidden', 'true')
  w.append(icon, el('p', 'welcome-title', greeting))
  return w
}

export interface IdentityFormRefs {
  root: HTMLFormElement
  name: HTMLInputElement
  email: HTMLInputElement
  error: HTMLElement
  submit: HTMLButtonElement
}

/**
 * Pre-chat form for `ask` projects: the visitor says who they are before
 * the composer appears. Self-declared, never proof — the server marks the
 * contact accordingly.
 */
export function buildIdentityForm(onSubmit: (visitor: Visitor) => void): IdentityFormRefs {
  const root = el('form', 'identity')
  root.noValidate = true
  root.append(el('p', 'identity-intro', 'Tell us who you are and we\u2019ll get straight to it.'))

  function field(label: string, type: 'text' | 'email', autocomplete: AutoFill): HTMLInputElement {
    const wrap = el('label', 'identity-field')
    wrap.append(el('span', 'identity-label', label))
    const input = el('input')
    input.type = type
    input.autocomplete = autocomplete
    input.required = true
    wrap.appendChild(input)
    root.appendChild(wrap)
    return input
  }
  const name = field('Your name', 'text', 'name')
  name.maxLength = 120
  const email = field('Your email', 'email', 'email')
  email.maxLength = 200

  const error = el('div', 'identity-error')
  error.setAttribute('role', 'alert')
  error.hidden = true
  const submit = button('btn btn-primary identity-submit', 'Continue')
  submit.type = 'submit'
  root.append(error, submit)

  root.addEventListener('submit', (event) => {
    event.preventDefault()
    const visitor = { name: name.value.trim(), email: email.value.trim() }
    // Both are required, and the email has to look like one: the API
    // refuses the rest, and a round-trip to learn that is worse.
    const invalid = !visitor.name
      ? 'Please tell us your name.'
      : !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(visitor.email)
        ? 'Please enter an email we can reply to.'
        : ''
    error.textContent = invalid
    error.hidden = !invalid
    if (invalid) {
      ;(visitor.name ? email : name).focus()
      return
    }
    onSubmit(visitor)
  })

  return { root, name, email, error, submit }
}

export function buildTyping(): HTMLElement {
  const t = el('div', 'typing')
  t.setAttribute('aria-hidden', 'true')
  t.innerHTML = '<span></span><span></span><span></span>'
  return t
}

export function buildNotice(text: string): HTMLElement {
  return el('div', 'system-event notice', text)
}

export function buildCsatCard(onPick: (satisfied: boolean) => void): HTMLElement {
  const card = el('div', 'csat-card')
  const row = el('div', 'csat-row')
  const up = button('btn btn-secondary', 'Satisfied', `${ICON_THUMBS_UP}<span>Helpful</span>`)
  const down = button('btn btn-secondary', 'Not satisfied', `${ICON_THUMBS_DOWN}<span>Not really</span>`)
  const pick = (satisfied: boolean) => {
    up.disabled = true
    down.disabled = true
    onPick(satisfied)
  }
  up.addEventListener('click', () => pick(true))
  down.addEventListener('click', () => pick(false))
  row.append(up, down)
  card.append(el('div', 'csat-q', 'How was this conversation?'), row)
  return card
}

export function buildCsatThanks(): HTMLElement {
  const card = el('div', 'csat-card')
  card.appendChild(el('div', 'csat-done', 'Thanks for the feedback!'))
  return card
}

export function buildResolvedActions(onStartNew: () => void): HTMLElement {
  const wrap = el('div', 'resolved-actions')
  const btn = el('button', 'btn btn-secondary', 'Start a new conversation')
  btn.type = 'button'
  btn.addEventListener('click', onStartNew)
  wrap.appendChild(btn)
  return wrap
}

// "Today", "Yesterday" or a date, between messages from different days.
export function buildDayDivider(iso: string): HTMLElement {
  const day = new Date(iso)
  const today = new Date()
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const diff = Math.round((startOf(today) - startOf(day)) / 86_400_000)
  const label =
    diff === 0
      ? 'Today'
      : diff === 1
        ? 'Yesterday'
        : day.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: diff > 300 ? 'numeric' : undefined })
  return el('div', 'day-divider', label)
}

export function buildHistoryList(
  items: Conversation[] | null,
  onPick: (id: string) => void,
): HTMLElement {
  const wrap = el('div', 'history-list')
  if (!items) {
    wrap.appendChild(el('div', 'history-empty', 'Loading…'))
    return wrap
  }
  if (items.length === 0) {
    wrap.appendChild(el('div', 'history-empty', 'No past conversations yet.'))
    return wrap
  }
  for (const it of items) {
    const row = el('button', 'history-row')
    row.type = 'button'
    row.append(
      el('div', 'history-row-preview', it.lastMessagePreview || '(no preview)'),
      el(
        'div',
        'history-row-meta',
        new Date(it.lastMessageAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }),
      ),
    )
    row.addEventListener('click', () => onPick(it.id))
    wrap.appendChild(row)
  }
  return wrap
}
